import type { ReactNode } from "react";
import { createContext, use, useCallback, useMemo, useState } from "react";

export type IBreadcrumbItem = {
  id: string;
  title: ReactNode;
  loading: boolean;
  order: number;
  backUrl?: string;
};

type IPageTitleState = {
  items: IBreadcrumbItem[];
};

type IPageTitleActions = {
  register: (item: IBreadcrumbItem) => void;
  update: (id: string, updates: Partial<Omit<IBreadcrumbItem, "id">>) => void;
  unregister: (id: string) => void;
};

const PageTitleStateContext = createContext<IPageTitleState>({ items: [] });
const PageTitleActionsContext = createContext<IPageTitleActions | null>(null);

export const PageTitleProvider = ({ children }: { children: ReactNode }) => {
  const [items, setItems] = useState<IBreadcrumbItem[]>([]);

  const register = useCallback((item: IBreadcrumbItem) => {
    setItems((prev) => {
      const exists = prev.find((i) => i.id === item.id);
      if (exists) {
        return prev.map((i) => (i.id === item.id ? item : i));
      }
      return [...prev, item].sort((a, b) => a.order - b.order);
    });
  }, []);

  const update = useCallback(
    (id: string, updates: Partial<Omit<IBreadcrumbItem, "id">>) => {
      setItems((prev) => {
        return prev.map((item) => {
          return item.id === id ? { ...item, ...updates } : item;
        });
      });
    },
    [],
  );

  const unregister = useCallback((id: string) => {
    setItems((prev) => {
      return prev.filter((i) => i.id !== id);
    });
  }, []);

  const stateValue = useMemo(() => {
    return { items };
  }, [items]);
  const actionsValue = useMemo(() => {
    return { register, update, unregister };
  }, [register, update, unregister]);

  return (
    <PageTitleActionsContext value={actionsValue}>
      <PageTitleStateContext value={stateValue}>
        {children}
      </PageTitleStateContext>
    </PageTitleActionsContext>
  );
};

export const usePageTitleState = () => {
  return use(PageTitleStateContext);
};

export const usePageTitleActions = () => {
  const context = use(PageTitleActionsContext);
  if (!context) {
    throw new Error(
      "usePageTitleActions must be used within PageTitleProvider",
    );
  }
  return context;
};
